import type { Editor } from '../editor/editor';
import type { History } from '../editor/history';
import { checkLayout, externalPrompt, LAYOUT_MAX_PHOTOS, LAYOUT_SIZES, layoutTemplate, pastedLayoutOf } from '../templates/aiLayout';
import { BUILTIN_TEMPLATES, tplPhotoSlots } from '../templates/builtin';
import { api, type AiImageInfo, type MadeLayout, type RecentLayout } from '../sync/api';
import type { Saver } from '../sync/saver';
import { $, $in } from './dom';
import { closeSheets, isOpen, openSheet } from './sheets';
import { toast } from './toast';
import { makeRefPicture, REF_SIDE, type RefPicture } from './refPicture';
import { pageThumb } from './pageThumbs';

/*
 * AI สร้างเทมเพลต (0023, choices 26 ต.ค. 2569): เทมเพลต → "สร้างเทมเพลตด้วย AI" (accounts with the right "รูป AI").
 * The person describes what they want and picks a page size; the server asks Gemini 3.8 Flash for a layout and checks
 * it (src/templates/aiLayout.ts). The app checks it again with the same checker, builds the page with the ready-made
 * templates' builder, shows a preview, then the same three choices: แทนหน้านี้ / เพิ่มเป็นหน้าใหม่ / เปิดเป็นงานใหม่.
 * "เติมรูป AI ในช่องใส่รูปด้วย" (off at first) makes a picture for each slot with รูป AI (0022), one by one; a slot whose
 * picture fails stays empty and is said. Everything put right on the way is listed (notes), never dropped silently.
 * "เทมเพลต AI ล่าสุด" (0024, choice "เซิร์ฟเวอร์ไปรับผลเอง"): my layouts of the last 24 h — one made while this panel was
 * closed, or whose wait ended (iPad asleep), is collected by the server and can be opened here again without paying twice.
 * รูปตัวอย่าง (26 ต.ค. 17:38: 1 รูป · อัปโหลด หรือเลือกจากหน้าของงานที่เปิดอยู่ / เทมเพลตของกลุ่ม / เทมเพลตสำเร็จรูป · ไม่เก็บ):
 * shrunk to one JPG (src/ui/refPicture.ts), sent with the request, kept in this panel only until it is closed.
 */
export interface AiTemplateHooks {
  /** a Thai reason when the open page cannot be changed now (someone else's work, a page still loading), else null */
  cannotEdit: () => string | null;
  /** another job that blocks leaving the open work (a video export…), else null */
  busy: () => string | null;
  canUse: () => boolean;
  /** an active admin: sees ทดสอบ Gemini กับรูป */
  isAdmin: () => boolean;
}

const usd = (n: number) => n < 1 ? n.toFixed(3) : n.toFixed(2);

export function initAiTemplate(ed: Editor, history: History, saver: Saver, hooks: AiTemplateHooks) {
  const brief = $<HTMLTextAreaElement>('atBrief'), sizeSel = $<HTMLSelectElement>('atSize'), fill = $in('atFill');
  const go = $<HTMLButtonElement>('atGo'), out = $('atOut'), preview = $<HTMLImageElement>('atPreview');
  let info: AiImageInfo | null = null, infoError = '';
  let busy = false, started = 0, timer = 0;
  /** the page made, waiting to be put in */
  let made: { name: string; objects: string; w: number; h: number } | null = null;
  /** the รูปตัวอย่าง: held here only (never uploaded on its own, never stored), forgotten when the panel is opened anew */
  let ref: RefPicture | null = null, refBusy = false;

  const say = (text: string, bad = true) => { const m = $('atMsg'); m.textContent = text; m.hidden = !text; m.classList.toggle('ok', !bad); };
  const errText = (e: unknown) => e instanceof Error ? e.message : String(e);

  function sizes() {
    const here = { id: 'here', th: `ขนาดหน้าที่เปิดอยู่ ${Math.round(ed.doc.w)}×${Math.round(ed.doc.h)}`, w: Math.round(ed.doc.w), h: Math.round(ed.doc.h) };
    // a page the server would refuse (outside 200–10000 px or longer than 4:1) is not offered
    const usable = here.w >= 200 && here.h >= 200 && here.w <= 10000 && here.h <= 10000 && here.w / here.h <= 4 && here.h / here.w <= 4;
    return usable ? [here, ...LAYOUT_SIZES] : [...LAYOUT_SIZES];
  }
  function fillSizes() {
    const keep = sizeSel.value;
    sizeSel.textContent = '';
    for (const s of sizes()) sizeSel.append(Object.assign(document.createElement('option'), { value: s.id, textContent: s.th }));
    sizeSel.value = sizes().some(s => s.id === keep) ? keep : sizes()[0].id;
  }
  const chosenSize = () => sizes().find(s => s.id === sizeSel.value) ?? sizes()[0];

  function paint() {
    const off = !info ? (infoError || 'กำลังโหลดข้อมูล AI…')
      : !info.canUse ? 'บัญชีนี้ยังไม่มีสิทธิ์ "รูป AI" ขอให้ผู้ดูแลเปิดให้ที่ หลังบ้านผู้ดูแล → ผู้ใช้'
        : info.problem ? info.problem
          : !info.layout ? 'เซิร์ฟเวอร์ยังเป็นรุ่นเก่าที่ไม่มี AI สร้างเทมเพลต (รอ deploy เสร็จแล้วโหลดหน้าใหม่)'
            : info.capUsd === 0 ? 'รูป AI และ AI สร้างเทมเพลตปิดอยู่ (เพดานต่อเดือน 0 ดอลลาร์) ผู้ดูแลตั้งเพดานได้ที่แผง "สร้างด้วย AI" ของช่องใส่รูป'
              : null;
    const prob = $('atProb');
    prob.textContent = off ?? ''; prob.hidden = !off || !info;
    $('atUse').textContent = info ? `เดือนนี้ทั้งกลุ่มใช้ไป $${usd(info.usedUsd)} จากเพดาน $${usd(info.capUsd)} (เพดานเดียวกับรูป AI · เริ่มใหม่ต้นเดือนตามเวลาไทย)` : (infoError || 'กำลังโหลด…');
    // a slot on a big page (A4, wide) may get a 2K picture: the higher price is shown when the page could have one
    const sz = chosenSize();
    const per = info ? info.estimates[Math.max(sz.w, sz.h) > info.twoKFromPx ? '2K' : '1K'] : 0;
    $('atFillNote').textContent = fill.checked
      ? `เติมรูปทุกช่อง (ไม่เกิน ${LAYOUT_MAX_PHOTOS} ช่อง) รูปละราว $${usd(per)} · รวมสูงสุดราว $${usd(per * LAYOUT_MAX_PHOTOS)}`
      : 'ปิดไว้ = ได้ช่องใส่รูปว่างไว้ใส่รูปเอง (หรือกด "สร้างด้วย AI" ทีละช่องภายหลัง)';
    $('atCost').textContent = info?.layout
      ? `โครงเทมเพลต 1 แบบ กันเงินไว้ไม่เกิน $${usd(info.layout.maxUsd)} (คิดจริงตามที่ Google นับ มักน้อยกว่า)` + (fill.checked ? ` + รูปในช่องตามด้านบน` : '')
      : '';
    if (!busy) go.textContent = made ? 'สร้างอีกแบบ' : 'สร้างเทมเพลต';
    go.disabled = busy || !!off;
    out.hidden = !made;
    for (const id of ['atReplace', 'atAdd', 'atNewWork']) ($(id) as HTMLButtonElement).disabled = busy;
    paintRef(off);
    // ใช้ AI อื่น: only the right matters (no key, no cap — nothing is paid here)
    const noRight = hooks.canUse() ? null : 'บัญชีนี้ยังไม่มีสิทธิ์ "รูป AI" ขอให้ผู้ดูแลเปิดให้ที่ หลังบ้านผู้ดูแล → ผู้ใช้';
    for (const id of ['atExtCopy', 'atExtGo']) ($(id) as HTMLButtonElement).disabled = busy || !!noRight;
    if (noRight) sayExt(noRight);
  }

  /* ---------- รูปตัวอย่าง (26 ต.ค.) ---------- */

  /** why a รูปตัวอย่าง cannot be added now, else null (a server without it would ignore the picture: said, not sent) */
  const refOff = (off: string | null) => off ?? (info?.layout && !info.layout.refMaxBytes ? 'เซิร์ฟเวอร์ยังเป็นรุ่นเก่าที่ไม่รับรูปตัวอย่าง (รอ deploy เสร็จแล้วโหลดหน้าใหม่)' : null);
  function paintRef(off: string | null) {
    const img = $<HTMLImageElement>('atRefImg'), no = refOff(off);
    img.hidden = !ref;
    if (ref) { if (img.src !== ref.url) img.src = ref.url; } else img.removeAttribute('src');
    const up = $<HTMLButtonElement>('atRefUpload'), pick = $<HTMLButtonElement>('atRefPick'), drop = $<HTMLButtonElement>('atRefDrop');
    up.disabled = pick.disabled = busy || refBusy || !!no;
    drop.hidden = !ref; drop.disabled = busy || refBusy;
    up.textContent = refBusy ? 'กำลังเตรียมรูป…' : ref ? 'เปลี่ยนรูป' : 'อัปโหลดรูป';
    $('atRefNote').textContent = ref
      ? `แนบ: ${ref.from} (${ref.w}×${ref.h} px) · AI ใช้แค่สไตล์ ไม่คัดลอกข้อความ โลโก้ หรือชื่อแบรนด์ · รูปไม่ถูกเก็บไว้ในระบบ ใช้ได้จนปิดแผงนี้`
      : no && info ? `แนบรูปตัวอย่างไม่ได้ตอนนี้: ${no}`
        : 'AI ใช้แค่สไตล์ของรูป (การจัดวาง สัดส่วน สี) ไม่คัดลอกข้อความ โลโก้ หรือชื่อแบรนด์ · รูปไม่ถูกเก็บไว้ในระบบ ใช้ได้จนปิดแผงนี้';
  }
  /** a picture → the รูปตัวอย่าง; one that cannot be used is refused with the reason and the old one stays */
  async function takeRef(src: Blob | string | (() => Promise<string>), from: string) {
    if (busy || refBusy) return;
    refBusy = true; say(''); paint();
    try {
      const pic = await makeRefPicture(typeof src === 'function' ? await src() : src, from, info?.layout?.refMaxBytes ?? 1_500_000);
      ref = pic;
      $('atRefPicker').hidden = true;
    } catch (e) { say(`ใช้รูปนี้เป็นรูปตัวอย่างไม่ได้: ${errText(e)}`); }
    finally { refBusy = false; paint(); }
  }
  /** a picture button of the picker */
  function refButton(label: string, thumb: string | Promise<string>, use: () => void) {
    const b = document.createElement('button');
    b.type = 'button'; b.title = label;
    const im = document.createElement('img');
    im.alt = ''; im.loading = 'lazy';
    if (typeof thumb === 'string') im.src = thumb;
    else thumb.then(u => { im.src = u; }, e => { im.alt = '!'; b.title = `${label} · แสดงตัวอย่างไม่ได้: ${errText(e)}`; });
    b.append(im, Object.assign(document.createElement('small'), { textContent: label }));
    b.onclick = use;
    return b;
  }
  async function openPicker() {
    const box = $('atRefPicker');
    if (!box.hidden) { box.hidden = true; return; }
    box.hidden = false;
    // the pages of the open work (drawn now, from what is on them)
    const pagesBox = $('atRefPages');
    pagesBox.textContent = '';
    if (history.loading) pagesBox.append(Object.assign(document.createElement('small'), { className: 'hint', textContent: 'หน้ากำลังโหลดอยู่ ปิดแล้วเปิดรายการนี้อีกครั้ง' }));
    else for (let k = 0; k < history.pageCount(); k++) {
      const json = history.pageJson(k), size = history.pageSize(k);
      pagesBox.append(refButton(`หน้า ${k + 1}`, pageThumb(json, size, ed.doc, 240), () => void takeRef(() => pageThumb(json, size, ed.doc, REF_SIDE), `หน้า ${k + 1} ของงานนี้`)));
    }
    // the ready-made templates
    const builtinBox = $('atRefBuiltin');
    if (!builtinBox.childElementCount) for (const t of BUILTIN_TEMPLATES) builtinBox.append(refButton(t.th, `/tpl/${t.id}.webp`, () => void takeRef(`/tpl/${t.id}.webp`, `เทมเพลตสำเร็จรูป "${t.th}"`)));
    // the group's templates (design ones; a video template's picture is its poster, not a layout)
    const groupBox = $('atRefGroup');
    groupBox.textContent = '';
    groupBox.append(Object.assign(document.createElement('small'), { className: 'hint', textContent: 'กำลังโหลด…' }));
    try {
      const list = (await api.templates()).templates.filter(t => t.kind !== 'video' && !t.hidden);
      groupBox.textContent = '';
      if (!list.length) groupBox.append(Object.assign(document.createElement('small'), { className: 'hint', textContent: 'กลุ่มยังไม่มีเทมเพลต' }));
      for (const t of list) groupBox.append(refButton(t.name, t.thumb, () => void takeRef(t.thumb, `เทมเพลตของกลุ่ม "${t.name}"`)));
    } catch (e) {
      groupBox.textContent = '';
      groupBox.append(Object.assign(document.createElement('small'), { className: 'hint', textContent: `โหลดเทมเพลตของกลุ่มไม่สำเร็จ: ${errText(e)}` }));
    }
  }

  async function loadInfo() {
    try { info = await api.aiImage.info(); infoError = ''; }
    catch (e) { info = null; infoError = 'โหลดข้อมูล AI ไม่สำเร็จ: ' + errText(e); }
    paint();
  }

  function open() {
    if (saver.mode !== 'online') { toast(`สร้างเทมเพลตด้วย AI ต้องออนไลน์ (${saver.reason})`, true); return; }
    // opened anew: the รูปตัวอย่าง of last time is forgotten (choice: ใช้ครั้งเดียว ไม่เก็บ)
    if (!isOpen('shAiTpl') && !refBusy) { ref = null; $('atRefPicker').hidden = true; }
    fillSizes();
    if (!busy && !made) say('');
    paint();
    openSheet('shAiTpl');
    void loadInfo();
    void loadRecent();
    $('atProbe').hidden = !hooks.isAdmin();
  }

  /** ask for a layout, check it, (pictures,) build the page and show it */
  async function make() {
    if (busy) return;
    if (refBusy) { say('รอเตรียมรูปตัวอย่างให้เสร็จก่อน'); return; }
    const words = brief.value.trim();
    if (!words) { say('พิมพ์ว่าอยากได้เทมเพลตแบบไหนก่อน'); brief.focus(); return; }
    const size = chosenSize();
    busy = true; made = null; say('');
    started = Date.now();
    const tick = (what: string) => () => { go.textContent = `${what}… ${Math.round((Date.now() - started) / 1000)} วิ`; };
    let step = tick('AI กำลังออกแบบ');
    step(); timer = window.setInterval(() => step(), 1000);
    paint();
    const spent = { usd: 0 };
    try {
      const r = await api.aiImage.layout({ brief: words, w: size.w, h: size.h, ...(ref ? { ref: { data: ref.data, type: ref.type } } : {}) });
      if (info) { info.usedUsd = r.usedUsd; info.capUsd = r.capUsd; }
      await present(r.layout, fill.checked, spent, s => { step = tick(s); });
    } catch (e) {
      say('สร้างเทมเพลตไม่สำเร็จ: ' + errText(e) + (spent.usd ? ` (คิดเงินไปแล้ว $${usd(spent.usd)})` : ''));
      if (!isOpen('shAiTpl')) toast('AI สร้างเทมเพลตไม่สำเร็จ: ' + errText(e), true);
      void loadInfo();
    } finally {
      busy = false; window.clearInterval(timer);
      paint();
      void loadRecent();
    }
  }

  /** a finished layout → checked with the same checker as the server (BUG-13) → (pictures,) built → shown with the three choices */
  async function present(layout: MadeLayout, withPictures: boolean, spent: { usd: number }, stepTo: (what: string) => void, from = '') {
    const notes: string[] = [];
    spent.usd += layout.costUsd;
    {
      const checked = checkLayout(layout.raw, layout.w, layout.h);
      if (!checked.ok) { say(`โครงที่ได้ใช้ไม่ได้: ${checked.error} (คิดเงิน $${usd(layout.costUsd)} แล้ว)`); return; }
      notes.push(...checked.value.notes);
      const t = layoutTemplate(`ai-${layout.id}`, checked.value, layout.w, layout.h);
      // เติมรูป AI: one slot after another; a failing one stops the rest (the next would most likely fail the same way)
      const pictures = new Map<number, string>();
      if (withPictures) {
        const slots = tplPhotoSlots(t);
        for (const sl of slots) {
          stepTo(`กำลังสร้างรูปช่องที่ ${sl.index + 1}/${slots.length}`);
          try {
            const p = await api.aiImage.make({ prompt: sl.hint, noText: true, w: sl.w, h: sl.h });
            pictures.set(sl.index, p.image.url); spent.usd += p.image.costUsd;
            if (info) { info.usedUsd = p.usedUsd; info.capUsd = p.capUsd; }
          } catch (e) {
            notes.push(`สร้างรูปช่องที่ ${sl.index + 1} ไม่สำเร็จ: ${errText(e)}` + (sl.index + 1 < slots.length ? ' · จึงหยุดช่องที่เหลือไว้ (ช่องยังว่าง ใส่รูปเองได้)' : ''));
            break;
          }
        }
      }
      stepTo('กำลังจัดหน้า');
      const { buildTemplate } = await import('../templates/build');
      const b = await buildTemplate(t, pictures);
      notes.push(...b.notes);
      const { renderThumbnail } = await import('../export/render');
      try { preview.src = await renderThumbnail(JSON.stringify({ objects: JSON.parse(b.objects) }), { w: b.w, h: b.h, name: t.th, bg: t.bg }, 480); }
      catch (e) { preview.removeAttribute('src'); notes.push(`ทำรูปตัวอย่างไม่ได้ (${errText(e)}) แต่ใส่ลงงานได้`); }
      made = { name: t.th, objects: b.objects, w: b.w, h: b.h };
      $('atName').textContent = `${t.th} · ${b.w}×${b.h} px · ` + (from || `คิดเงินรวม $${usd(spent.usd)}`);
      say(notes.length ? `ได้เทมเพลตแล้ว มีเรื่องที่ควรรู้: ${notes.join(' · ')}` : 'ได้เทมเพลตแล้ว เลือกว่าจะใส่แบบไหน', notes.length > 0);
      if (!isOpen('shAiTpl')) toast('AI สร้างเทมเพลตเสร็จแล้ว เปิด เทมเพลต → "สร้างเทมเพลตด้วย AI" เพื่อใส่ลงงาน');
    }
  }

  /* ---------- ทดสอบ Gemini กับรูป (admins, 26 ต.ค. 18:13) ---------- */

  let probing = false;
  async function probe() {
    if (probing) return;
    const btn = $<HTMLButtonElement>('atProbeGo'), box = $('atProbeOut');
    probing = true; btn.disabled = true; btn.textContent = 'กำลังทดสอบ… (ราว 1–2 นาที)';
    box.textContent = '';
    try {
      const r = await api.aiImage.probe();
      for (const x of r.results) {
        const row = document.createElement('div');
        row.className = 'airow'; row.dataset.status = x.ok ? 'done' : 'failed';
        const tokens = x.inputTokens !== null || x.outputTokens !== null ? ` · token เข้า ${x.inputTokens ?? '?'} ออก ${x.outputTokens ?? '?'}` : '';
        const time = x.seconds !== undefined ? ` · ใช้เวลา ${x.seconds} วิ` + (x.firstSeconds !== undefined ? ` (เริ่มตอบที่ ${x.firstSeconds} วิ)` : '') : '';
        row.append(Object.assign(document.createElement('b'), { textContent: `${x.ok ? '✓' : '✗'} ${x.th}` }),
          Object.assign(document.createElement('small'), { textContent: `${x.say}${x.model ? ` · รุ่นที่ Google ใช้: ${x.model}` : ''}${tokens}${time}` }));
        box.append(row);
      }
      box.append(Object.assign(document.createElement('small'), { className: 'hint', textContent: `ทดสอบเมื่อ ${timeOf(r.at)} · ถ่ายภาพหน้าจอนี้ส่งให้ Claude ได้เลย` }));
    } catch (e) { box.append(Object.assign(document.createElement('p'), { className: 'admsg', textContent: 'ทดสอบไม่สำเร็จ: ' + errText(e) })); }
    finally { probing = false; btn.disabled = false; btn.textContent = 'เริ่มทดสอบ'; }
  }

  /* ---------- ใช้ AI อื่น (26 ต.ค. 21:47: วางโครง JSON · เฉพาะคนที่มีสิทธิ์ "รูป AI") ---------- */

  const sayExt = (text: string, bad = true) => { const m = $('atExtMsg'); m.textContent = text; m.hidden = !text; m.classList.toggle('ok', !bad); };
  /** the page size the instructions were copied for (the AI laid out that frame); null = none copied in this panel */
  let extSize: { w: number; h: number; th: string } | null = null;
  async function copyExternal() {
    const words = brief.value.trim();
    if (!words) { sayExt('พิมพ์ว่าอยากได้เทมเพลตแบบไหนในช่องด้านบนก่อน แล้วค่อยคัดลอกคำสั่ง'); brief.focus(); return; }
    const size = chosenSize();
    const text = externalPrompt(words, size.w, size.h);
    extSize = { w: size.w, h: size.h, th: size.th };
    const box = $<HTMLTextAreaElement>('atExtPrompt');
    try {
      await navigator.clipboard.writeText(text);
      box.hidden = true;
      sayExt(`คัดลอกคำสั่งแล้ว (${size.th}) วางในแอป AI ได้เลย`, false);
    } catch (e) {
      // no clipboard (permission, older browser): the text is shown to copy by hand — never a silent failure
      box.value = text; box.hidden = false; box.focus(); box.select();
      sayExt(`คัดลอกอัตโนมัติไม่ได้ (${errText(e)}) — กดค้างในช่องด้านบน เลือกทั้งหมด แล้วคัดลอกเอง`);
    }
  }
  async function fromPasted() {
    if (busy) return;
    if (!hooks.canUse()) { sayExt('บัญชีนี้ยังไม่มีสิทธิ์ "รูป AI"'); return; }
    const p = pastedLayoutOf($<HTMLTextAreaElement>('atExtPaste').value);
    if (!p.ok) { sayExt(p.error); return; }
    // the frame the AI was asked for; a size changed since copying is said, and the copied one is used
    const now = chosenSize(), size = extSize ?? now;
    const moved = extSize && (extSize.w !== now.w || extSize.h !== now.h) ? `ใช้ขนาด ${extSize.th} ตามคำสั่งที่คัดลอกไป (ไม่ใช่ขนาดที่เลือกอยู่ตอนนี้)` : '';
    // the same checker as Gemini's answers (BUG-13): a layout that cannot be used is said here, next to the paste box
    const checked = checkLayout(p.value, size.w, size.h);
    if (!checked.ok) { sayExt(`ใช้โครงที่วางไม่ได้: ${checked.error}`); return; }
    busy = true; made = null; say(''); sayExt(''); paint();
    try {
      await present({ id: `ext-${Date.now()}`, raw: p.value, name: '', notes: [], w: size.w, h: size.h, costUsd: 0 }, false, { usd: 0 }, () => {}, 'จาก AI อื่น · ไม่คิดเงินในระบบนี้');
      const fixed = checked.value.notes.length ? ` · มี ${checked.value.notes.length} เรื่องที่แก้ให้ (ดูข้อความด้านบน)` : '';
      if (made) sayExt(`สร้างแล้ว ดูตัวอย่างและเลือกวิธีใส่ด้านล่าง${moved ? ` · ${moved}` : ''}${fixed}`, !!(moved || fixed));
      else sayExt('ใช้โครงที่วางไม่ได้ ดูเหตุผลด้านบน');
    } catch (e) { sayExt('สร้างจากโครงที่วางไม่สำเร็จ: ' + errText(e)); }
    finally { busy = false; paint(); }
  }

  /* ---------- เทมเพลต AI ล่าสุด (0024) ---------- */

  let recentKey = '';
  /** the time of a row, in Thailand time (stored with +07:00) */
  const timeOf = (iso: string) => `${iso.slice(8, 10)}/${iso.slice(5, 7)} ${iso.slice(11, 16)} น.`;
  async function loadRecent() {
    const msg = $('atRecentMsg');
    $('atRecent').hidden = false;
    msg.textContent = 'กำลังเช็กงาน AI ที่ค้างอยู่…';
    let list: RecentLayout[];
    try { const r = await api.aiImage.recent(); list = r.layouts; if (info) { info.usedUsd = r.usedUsd; info.capUsd = r.capUsd; paint(); } }
    catch (e) { msg.textContent = 'โหลดเทมเพลต AI ล่าสุดไม่สำเร็จ: ' + errText(e); return; }
    msg.textContent = list.length ? 'แตะแบบที่เสร็จแล้วเพื่อเปิดอีกครั้ง (ไม่คิดเงินซ้ำ)' : 'ยังไม่มีเทมเพลต AI ใน 24 ชั่วโมงที่ผ่านมา';
    const key = JSON.stringify(list.map(l => [l.id, l.status]));
    if (key === recentKey) return;   // the same rows: leave the buttons as they are (CORE: never rebuild what may be touched)
    recentKey = key;
    const box = $('atRecentList');
    box.textContent = '';
    for (const l of list) {
      const b = document.createElement('button');
      b.className = 'airow'; b.type = 'button'; b.dataset.status = l.status;
      const what = l.status === 'done' ? (l.name ?? 'เทมเพลตจาก AI') : l.brief;
      const state = l.status === 'done' ? `${l.w}×${l.h} · $${usd(l.costUsd)}` : l.status === 'making' ? 'กำลังสร้าง…' : 'ไม่สำเร็จ (แตะดูเหตุผล)';
      b.append(Object.assign(document.createElement('b'), { textContent: what }), Object.assign(document.createElement('small'), { textContent: `${timeOf(l.createdAt)} · ${state}` }));
      b.onclick = () => void openRecent(l);
      box.append(b);
    }
  }
  async function openRecent(l: RecentLayout) {
    if (busy) { say('กำลังทำงานอื่นอยู่ รอให้เสร็จก่อน'); return; }
    if (l.status === 'failed') { say(`เทมเพลตนี้ไม่สำเร็จ: ${l.error ?? 'ไม่ทราบเหตุผล'}`); return; }
    if (l.status === 'making') { say('เทมเพลตนี้ AI ยังทำไม่เสร็จ ลองเปิดแผงนี้อีกครั้งภายหลัง (ไม่ต้องสร้างใหม่)', false); void loadRecent(); return; }
    busy = true; made = null; say(''); paint();
    try {
      const r = await api.aiImage.layoutJob(l.id);
      if (!r.layout) { say('เทมเพลตนี้ยังทำไม่เสร็จ ลองอีกครั้งภายหลัง', false); return; }
      // opened again: no new pictures (they would be paid for); the slots stay empty to fill
      await present(r.layout, false, { usd: 0 }, () => {});
    } catch (e) { say('เปิดเทมเพลต AI นี้ไม่สำเร็จ: ' + errText(e)); }
    finally { busy = false; paint(); }
  }

  /** "แทนหน้านี้" / "เพิ่มเป็นหน้าใหม่": into the work that is open (as the ready-made templates do) */
  function putIn(mode: 'replace' | 'add') {
    if (!made || busy) return;
    const no = hooks.cannotEdit() ?? hooks.busy();
    if (no) { say(`ใส่ลงงานนี้ไม่ได้: ${no} (เลือก "เปิดเป็นงานใหม่" ได้)`); return; }
    const why = history.putPages([{ objects: made.objects, size: { w: made.w, h: made.h } }], mode);
    if (why) { say(`ใส่เทมเพลตไม่ได้: ${why}`); return; }
    toast(mode === 'replace' ? `ใช้ "${made.name}" แทนหน้านี้แล้ว (กดเลิกทำเพื่อย้อนกลับได้)` : `เพิ่ม "${made.name}" เป็นหน้าใหม่แล้ว`);
    if (!matchMedia('(min-width: 900px)').matches) closeSheets();
  }
  async function newWork() {
    if (!made || busy) return;
    const other = hooks.busy();
    if (other) { say(`ยังเปิดงานใหม่ไม่ได้: ${other}`); return; }
    // taken and locked BEFORE waiting: "สร้างอีกแบบ" during the wait must not empty it or start another paid request
    const m = made;
    busy = true; paint();
    try {
      const why = await saver.flush();
      if (why) { say(`ยังเปิดงานใหม่ไม่ได้ เพราะ${why} เพื่อไม่ให้การแก้ไขหาย`); return; }
      await saver.newDesign();
      ed.doc.name = m.name;
      const no = history.putPages([{ objects: m.objects, size: { w: m.w, h: m.h } }], 'replace');
      if (no) { say(`เปิดเป็นงานใหม่ไม่ได้: ${no}`); return; }
      history.reset();   // the new work starts as the template
      saver.changed();
      closeSheets();
      toast(`เริ่มงานใหม่ "${m.name}" จาก AI แล้ว`);
    } catch (e) { say('เปิดเป็นงานใหม่ไม่สำเร็จ: ' + errText(e)); }
    finally { busy = false; paint(); }
  }

  go.onclick = () => void make();
  $('atExtCopy').onclick = () => void copyExternal();
  $('atExtGo').onclick = () => void fromPasted();
  $('atProbeGo').onclick = () => void probe();
  $('atRefUpload').onclick = () => $<HTMLInputElement>('atRefFile').click();
  $<HTMLInputElement>('atRefFile').onchange = e => {
    const input = e.target as HTMLInputElement, f = input.files?.[0];
    input.value = '';   // the same file can be chosen again
    if (f) void takeRef(f, `ไฟล์ ${f.name}`);
  };
  $('atRefPick').onclick = () => void openPicker();
  $('atRefDrop').onclick = () => { ref = null; say(''); paint(); };
  fill.onchange = paint;
  sizeSel.onchange = paint;
  $('atReplace').onclick = () => putIn('replace');
  $('atAdd').onclick = () => putIn('add');
  $('atNewWork').onclick = () => void newWork();
  $('tplAiNew').onclick = open;
  /** the button in เทมเพลต: design templates only, for accounts that may use it (the server checks too) */
  const showButton = () => {
    const design = document.querySelector('#tplKind [data-v="design"]')?.getAttribute('aria-pressed') !== 'false';
    $('tplAiNewRow').hidden = !hooks.canUse() || !design;
  };
  $('tplKind').addEventListener('click', () => setTimeout(showButton));
  return { open, showButton };
}
